/**
 * iconScatter.ts
 * مولّد مواضع رموز الطقس المبعثَرة (مطر/ثلج/برق) فوق الخريطة.
 *
 * لماذا وُحِّد هنا: كانت كل طبقة رموز تمشي على شبكة بكسل **منتظمة تماماً**
 * (`x = n·spacing`)، فتظهر الرموز صفوفاً وأعمدة مستقيمة تُقرأ فوراً كنمط صناعي لا
 * كهطول. الحلّ ليس عشوائية كاملة (تُنتج تكتّلات وفجوات) بل **شبكة مزاحة عشوائياً**
 * (jittered grid): خليّة ثابتة الحجم، والرمز يقع في نقطة شبه‑عشوائية داخلها. النتيجة
 * كثافة منتظمة بلا أي محور مستقيم مرئي — وهو ما يعطي الملمس الطبيعي.
 *
 * ثلاث خصائص مقصودة:
 *  1. **التباعد بالبكسل على الشاشة**، لا بالدرجات: عدد الرموز لكل مساحة شاشة يبقى
 *     ثابتاً عند أي تكبير/تصغير — لا يتناقص كلما صُغِّرت الخريطة.
 *  2. **الإزاحة مشتقّة من موقع الخلية في فضاء بكسل العالم** (مرساة إحداثي 0°,0°) لا من
 *     موقعها على الشاشة. لذلك يحتفظ كل رمز بإزاحته نفسها أثناء التحريك عند نفس مستوى
 *     التكبير، فلا "يزحف" النمط ولا يومض عند كل إعادة بناء.
 *  3. **أربعة أعداد شبه‑عشوائية مستقرّة** لكل خليّة: تُستعمل للإزاحة والدوران والحجم
 *     وتأخير الحركة — كلها ثابتة لنفس الخلية، فلا ترتجف بين البناءات، و**كلٌّ منها من
 *     تجزئة مستقلّة**. لا تُعِد استعمال تجزئة موضعٍ (r1/r2) في اختيار من يُرسم: كان
 *     التخفيف يجري بـ r1 نفسه، فلا ينجو إلا ما إزاحته صغيرة — أي ما يقع عند الحافّة
 *     اليسرى لخليّته — فتظهر أعمدة مستقيمة، وهو بالضبط ما تمنعه الشبكة المزاحة.
 */
import type { Map as MaplibreMap } from 'maplibre-gl';

/** خليّة مرشّحة: موقع جغرافي + أربعة أعداد شبه‑عشوائية ثابتة لها. */
export interface ScatterCell {
    lon: number;
    lat: number;
    /** **مركز الخليّة** على الشاشة بالبكسل (لا النقطة المزاحة) — منه تُشتقّ رقعة الخليّة
     *  الجغرافية بإسقاط عكسي لركنَيها، كما يفعل مسح البرق بدقّة الراستر عند التصغير.
     *
     *  المركز لا النقطة المزاحة عمداً: صندوق بحجم الخليّة حول نقطة مزاحة يتداخل مع صناديق
     *  جيرانه، فتجد خليّتان متجاورتان الذروة نفسها وتضعان رمزين فوق بعضهما. الصناديق
     *  المبنيّة على المراكز تُغطّي الشاشة تغطيةً تامّة بلا تداخل: ذروة واحدة لكل رقعة. */
    x: number;
    y: number;
    /** إزاحة أفقية داخل الخلية. */ r1: number;
    /** دوران (وإزاحة رأسية). */    r2: number;
    /** حجم. */                    r3: number;
    /** توقيت الحركة. */            r4: number;
}

/** نسبة الخلية المسموح للرمز أن يتحرّك ضمنها (0.9 = من 5% إلى 95% من عرض الخلية). */
const JITTER_SPAN = 0.9;
const JITTER_MIN = (1 - JITTER_SPAN) / 2;

/** تجزئة عدد صحيح ثنائية الأبعاد → [0,1). مستقرّة ورخيصة (لا Math.random). */
function hash01(i: number, j: number, salt: number): number {
    let h = Math.imul(i, 0x27d4eb2d) ^ Math.imul(j, 0x165667b1) ^ Math.imul(salt, 0x9e3779b1);
    h = Math.imul(h ^ (h >>> 15), 0x85ebca6b);
    h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35);
    return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

/**
 * يمرّ على كل خلايا الشاشة بالتباعد المطلوب ويستدعي `visit` بموقع مُزاح داخل كل خلية.
 * الكائن المُمرَّر **يُعاد استخدامه** بين النداءات — على المستدعي نسخ ما يحتاجه، وهذا
 * مقصود: الشبكة قد تبلغ آلاف الخلايا في كل إعادة بناء.
 */
export function forEachScatterCell(
    map: MaplibreMap,
    spacingPx: number,
    visit: (cell: ScatterCell) => void,
): void {
    const canvas = map.getCanvas();
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    if (w === 0 || h === 0 || spacingPx <= 0) return;

    // مرساة النمط: بكسل الشاشة الذي يقع عنده الإحداثي 0°,0°. فهرس الخلية يُحسب نسبةً
    // إليه لا إلى ركن الشاشة، فيبقى النمط مثبَّتاً على الأرض أثناء التحريك.
    const ref = map.project([0, 0]);
    if (!Number.isFinite(ref.x) || !Number.isFinite(ref.y)) return;

    const i0 = Math.floor(-ref.x / spacingPx);
    const j0 = Math.floor(-ref.y / spacingPx);
    // حدّ أعلى صريح لعدد الخلايا: يحمي من مدى شاذّ لو أعاد الإسقاط قيمة ضخمة.
    const iCount = Math.ceil(w / spacingPx) + 2;
    const jCount = Math.ceil(h / spacingPx) + 2;

    const cell: ScatterCell = { lon: 0, lat: 0, x: 0, y: 0, r1: 0, r2: 0, r3: 0, r4: 0 };
    for (let jj = 0; jj < jCount; jj++) {
        const j = j0 + jj;
        for (let ii = 0; ii < iCount; ii++) {
            const i = i0 + ii;
            const r1 = hash01(i, j, 1);
            const r2 = hash01(i, j, 2);
            const sx = ref.x + (i + JITTER_MIN + r1 * JITTER_SPAN) * spacingPx;
            const sy = ref.y + (j + JITTER_MIN + r2 * JITTER_SPAN) * spacingPx;
            if (sx < 0 || sx >= w || sy < 0 || sy >= h) continue;
            const { lng, lat } = map.unproject([sx, sy]);
            // خارج مدى مركاتور الصالح (أو إسقاط شاذّ) — لا بيانات هناك أصلاً.
            if (!Number.isFinite(lat) || !Number.isFinite(lng) || lat > 84 || lat < -84) continue;
            cell.lon = lng;
            cell.lat = lat;
            cell.x = ref.x + (i + 0.5) * spacingPx;
            cell.y = ref.y + (j + 0.5) * spacingPx;
            cell.r1 = r1;
            cell.r2 = r2;
            cell.r3 = hash01(i, j, 3);
            cell.r4 = hash01(i, j, 4);
            visit(cell);
        }
    }
}
